import environment from 'platform/utilities/environment';

let zipValidationCache = null; // { zip: '12345', isValid: true/false }

export const updateZipCache = (zip, isValid) => {
  zipValidationCache = { zip, isValid };
};

export const clearZipCache = () => {
  zipValidationCache = null;
};

// Prefill zip verification result, kept SEPARATE from the interactive cache
// above. This store is read only by the submit-time check
// (getCustomValidationErrors), so a prefilled invalid zip surfaces on the
// review page WITHOUT affecting the per-page validateZIP calls (which would
// otherwise block Continue mid-form). Shape: { zip: '12345', isValid: bool }.
let prefillZipResult = null;

export const clearPrefillZipResult = () => {
  prefillZipResult = null;
};

/** @returns {{zip: string, isValid: boolean}|null} last prefill verification */
export const getPrefillZipResult = () => prefillZipResult;

/**
 * Verify a prefilled zip the user may never edit, and stash the result in the
 * prefill-only store above. The ZipCodeInput component only fires the zip API
 * on keystroke, so an invalid prefilled zip is otherwise never verified and
 * slips past validation entirely (the prefill bypass bug). onFormLoaded calls
 * this so the submit-time check has a result to read.
 *
 * @param {string} zip - Raw zip field value (5- or 9-digit; only first 5 used)
 * @returns {Promise<boolean|null>} verified validity, or null if undeterminable
 */
export const verifyPrefilledZipCode = async zip => {
  const fiveDigit = String(zip ?? '').slice(0, 5);
  if (!/^\d{5}$/.test(fiveDigit)) {
    clearPrefillZipResult();
    return null;
  }

  try {
    const resp = await fetch(
      `${environment.API_URL}/validations/v0/zipcode/${fiveDigit}`,
      { credentials: 'include' },
    );
    if (!resp.ok) {
      throw new Error(`Zip code validation failed (${resp.status})`);
    }
    const data = await resp.json();
    const isValid = data?.zip_is_valid;
    if (typeof isValid !== 'boolean') {
      throw new Error(
        'Zip code validation response missing zip_is_valid boolean',
      );
    }
    prefillZipResult = { zip: fiveDigit, isValid };
    return isValid;
  } catch (err) {
    // Couldn't verify — drop the result so the submit check doesn't block on a
    // network hiccup (fall through to "assume valid").
    clearPrefillZipResult();
    window.DD_RUM?.addError?.(err, { source: 'zip_code_validation_prefill' });
    return null;
  }
};

export const isValidZIP = value => {
  const str = String(value);
  if (!/^\d{5}(?:(?:[-\s])?\d{4})?$/.test(str)) return false;
  const fiveDigit = str.slice(0, 5);
  if (zipValidationCache?.zip === fiveDigit) {
    return zipValidationCache.isValid;
  }
  return true;
};
